export const salesArticles = [
    {
        id: 'sales.overview',
        title: 'Sales Pipeline Overview',
        category: 'Sales',
        order: 1,
        summary: 'Tracking enquiries from first contact to a won job',
        content: `
The Sales page tracks every enquiry (lead) from first contact through to a signed job — the pipeline for your sales team.

**The pipeline stages:**

New Lead → Contacted → Qualified → Quoting → Tender Sent → **Won** / **Lost**

**Key ideas:**

- A **lead** is a prospective build: the person, the block, the home they're after and its estimated value
- Everything that happens is logged on the lead's **activity timeline** — calls, emails, meetings, notes and follow-up tasks with due dates
- Winning a lead **converts it to a Job** with one click — the New Job form is pre-filled from the lead
- Leads never pollute your Jobs list: a job only exists once you win
- Leads are numbered **L-0001, L-0002 …**

**Who can do what:** a **viewer** sees the board read-only. Any other role creates and works leads, logs activities and moves stages. Only an **owner, admin or manager** deletes a lead or **reassigns its owner** (the owner must be a member of this workspace).
    `,
        expandableSections: [
            {
                title: 'The Pipeline Board',
                content: `
The board shows a column per stage with each lead as a card:

- **Drag a card between columns** to move it through the pipeline
- Dropping on **Lost** asks for a loss reason (feeds win/loss reporting)
- Each column header shows the count and total estimated value in that stage
- Cards show the contact, suburb, value, and a badge for open follow-up tasks (red when overdue)
- Tick **Won & lost** to see closed leads; use the list toggle for a sortable table view
- Search matches any words in any order — lead number, title, contact, suburb, source
- **Owner** — each lead shows its owner (the staff member responsible); the **owner filter** narrows the board/list to *My leads*, a specific person, or *Unassigned*
        `,
            },
            {
                title: 'Working a Lead',
                content: `
Click a card to open the lead:

1. **Details** — contact info, source, estimated value, expected close, site address, notes
2. **Activity** — log a Note / Call / Email / Meeting, or add a **Task** with a due date (YYYY-MM-DD); tick tasks off as you complete them. Stage changes are logged automatically and those entries cannot be deleted
3. **Stage buttons** — move the lead without going back to the board; stages are the fixed list above, and **Lost** asks for a reason (up to 2,000 characters)

Details are checked as you save: a lead needs a title, the estimated value must be a number, dates are YYYY-MM-DD.

Follow-up discipline: give every active lead an open task — the board badge tells you who's waiting on you.
        `,
            },
            {
                title: 'Tender Register',
                content: `
Any lead can carry a **tender entry** — the fields under *Tender register* on the lead:

- **Closes on / time** — the submission deadline. Setting it puts the lead on the register: the board card and the list show a countdown badge (blue → amber inside three days → red once it has passed), and the **Tenders** tick box on the Sales page shows only leads with a closing date, soonest first
- **Tender type** — invited, public or negotiated (used by the Win / Loss report)
- **Bid / no bid** — the go / no-go call. *No bid* asks for a reason and stops the reminders; the decision is logged on the timeline
- **Probability** — your chance of winning, 0–100 %. The **weighted value** (estimated value × probability) is shown on the lead, on the pipeline total at the top of the Sales page, and in the reports
- **Estimator** — who is pricing it (the owner still runs the lead). Both are reminded

**Reminders:** three days before the deadline, on the day, and the day after if the lead is still open, the owner and estimator get an in-app notice (the bell) that opens the lead. One notice per milestone.

**Tender documents:** drop the tender package, addenda and drawings onto the *Tender documents* panel on the lead (or click Upload). They are ordinary Documents filed against the lead, so they also appear in the Documents module.

**Reports → Sales:** *Tender Register* lists every tender with days left, decision, probability and weighted value; *Win / Loss* counts won and lost leads for a period grouped by source, owner, month, tender type or suburb, with the win rate, values, average days to close and the most common lost reason — alongside what is still open in each group. Ask Costed can read and update the register too ("what tenders close this week?").
        `,
            },
            {
                title: 'Winning: Convert to Job',
                content: `
When the client signs:

1. Open the lead and click **Convert to Job**
2. The New Job form appears **pre-filled** from the lead (name, client, site address) — set the job number and adjust anything
3. On create, the lead is marked **Won** and linked to the job (the job number shows on the lead card)

Tip: if the sale is based on a standard home design, use **BOQ → Copy from Job** on the new job to start the estimate from your template job for that model.
        `,
            },
        ],
        keywords: ['sales', 'crm', 'leads', 'pipeline', 'prospects', 'enquiry', 'won', 'lost', 'convert to job', 'follow up', 'board', 'kanban', 'tender', 'tender register', 'bid', 'no bid', 'probability', 'weighted', 'closing date', 'win loss', 'estimator'],
        route: '/sales',
        relatedArticles: ['quotes.overview', 'jobs.create'],
    },
];
